/**
 * Organizations API
 * RTK Query endpoints for organization management (admin only)
 */

import { baseApi } from "./baseApi";

/**
 * Organization interface
 */
export interface Organization {
	id: string;
	name: string;
	email?: string;
	phone?: string;
	address?: string;
	city?: string;
	state?: string;
	zip_code?: string;
	country?: string;
	status: "active" | "inactive" | "suspended";
	bank_name?: string;
	account_number?: string;
	account_name?: string;
	created_at: string;
	updated_at: string;
}

/**
 * Organization list response interface
 */
export interface OrganizationListResponse {
	count: number;
	next: string | null;
	previous: string | null;
	results: Organization[];
}

/**
 * Organizations API endpoints
 */
export const organizationsApi = baseApi.injectEndpoints({
	endpoints: (builder) => ({
		/**
		 * List all organizations (admin only)
		 */
		listOrganizations: builder.query<
			OrganizationListResponse,
			{
				page?: number;
				page_size?: number;
				status?: string;
				search?: string;
				ordering?: string;
			}
		>({
			query: (params = {}) => {
				const queryParams = new URLSearchParams();
				if (params.page) queryParams.append("page", params.page.toString());
				if (params.page_size)
					queryParams.append("page_size", params.page_size.toString());
				if (params.status) queryParams.append("status", params.status);
				if (params.search) queryParams.append("search", params.search);
				if (params.ordering) queryParams.append("ordering", params.ordering);

				return {
					url: `/organizations/?${queryParams.toString()}`,
					method: "GET",
				};
			},
			providesTags: ["Organizations"],
		}),

		/**
		 * Get organization by ID (admin only)
		 */
		getOrganization: builder.query<Organization, string>({
			query: (id) => ({
				url: `/organizations/${id}/`,
				method: "GET",
			}),
			providesTags: (result, error, id) => [{ type: "Organizations", id }],
		}),

		/**
		 * Create organization (admin only)
		 */
		createOrganization: builder.mutation<Organization, Partial<Organization>>({
			query: (data) => ({
				url: "/organizations/",
				method: "POST",
				body: data,
			}),
			invalidatesTags: ["Organizations"],
		}),

		/**
		 * Update organization (admin only)
		 */
		updateOrganization: builder.mutation<
			Organization,
			{ id: string; data: Partial<Organization> }
		>({
			query: ({ id, data }) => ({
				url: `/organizations/${id}/`,
				method: "PATCH",
				body: data,
			}),
			invalidatesTags: (result, error, { id }) => [
				{ type: "Organizations", id },
				{ type: "Organizations", id: "LIST" },
			],
		}),

		/**
		 * Delete organization (admin only)
		 */
		deleteOrganization: builder.mutation<void, string>({
			query: (id) => ({
				url: `/organizations/${id}/`,
				method: "DELETE",
			}),
			invalidatesTags: ["Organizations"],
		}),
	}),
});

/**
 * Export hooks for usage in functional components
 */
export const {
	useListOrganizationsQuery,
	useLazyListOrganizationsQuery,
	useGetOrganizationQuery,
	useCreateOrganizationMutation,
	useUpdateOrganizationMutation,
	useDeleteOrganizationMutation,
} = organizationsApi;
